import { useTranslation } from 'react-i18next';
import { LockClosedIcon } from '@heroicons/react/24/outline';
import { Button } from '@leadsales/design-system/react';

export type tWapiPlanReadOnlyBannerProps = {
  /**
   * Owner or Admin. The mounter decides the role and passes the answer, so
   * this component never reads it (D7: the role is read in one place).
   */
  canUpgradePlan: boolean;
  /** Called by «Mejorar plan». The mounter owns the navigation. */
  onUpgradePlan: () => void;
  /**
   * The workspace has WhatsApp Web connected, so this lead can be answered
   * there. The mounter decides it with the same reading as the
   * `wapiBanner.migratedToWeb` notice.
   */
  canReplyByWhatsAppWeb: boolean;
  /** Called by «Responder por WhatsApp Web». The mounter owns the switch. */
  onReplyByWhatsAppWeb: () => void;
  /**
   * The user already asked for WhatsApp Web and the chat still resolved to
   * WhatsApp API (RET-2349). The banner says so, and does not offer the
   * same switch again. The mounter decides it from the requested channel.
   */
  webSwitchFailed: boolean;
};

/**
 * RET-2339. Composer replacement for a WhatsApp API chat in a workspace whose
 * plan does not include WhatsApp API (Básico, or Legacy without the WBS
 * access exception). The history stays visible; the input, the "+" menu, the
 * seal and the no-balance bar are unmounted by the mounter, not disabled.
 *
 * With WhatsApp Web connected, every role gets «Responder por WhatsApp Web»
 * as the primary action, and Owner and Admin also get «Mejorar plan» as the
 * secondary one (product, 2026-10-07). Without WhatsApp Web, Owner and Admin
 * get «Mejorar plan» and everyone else is told whom to ask, with no button.
 * When `webSwitchFailed`, the switch was already tried: the text tells the
 * user to find the chat among their WhatsApp Web chats, the web button and the
 * seller hint are gone, and Owner and Admin keep «Mejorar plan».
 * `role="status"` like `BlockedContactBanner`; the lock is decorative. The
 * text and the buttons wrap onto two lines when the composer is narrow.
 */
export default function WapiPlanReadOnlyBanner(
  props: tWapiPlanReadOnlyBannerProps,
) {
  const {
    canUpgradePlan,
    onUpgradePlan,
    canReplyByWhatsAppWeb,
    onReplyByWhatsAppWeb,
    webSwitchFailed,
  } = props;
  const { t } = useTranslation('chat/chat');
  const offersWeb = canReplyByWhatsAppWeb && !webSwitchFailed;
  const message = webSwitchFailed
    ? t('wapiPlanReadOnly.webSwitchFailed')
    : offersWeb
      ? t('wapiPlanReadOnly.messageWithWeb')
      : t('wapiPlanReadOnly.message');

  return (
    <div
      role="status"
      data-testid="wapi-plan-read-only-banner"
      className="flex w-full flex-wrap items-center gap-x-3 gap-y-2 rounded-[var(--ls-radius-lg)] border border-[var(--ls-border-neutral-light)] bg-[var(--ls-surface-background)] px-4 py-3"
    >
      <div className="flex min-w-0 flex-1 basis-64 items-start gap-2">
        <LockClosedIcon
          aria-hidden="true"
          data-testid="wapi-plan-read-only-lock"
          className="mt-0.5 h-4 w-4 shrink-0 text-[var(--ls-accent-neutral-default)]"
        />
        <p className="text-sm text-[var(--ls-text-neutral-default)]">
          {message}
          {canUpgradePlan || offersWeb || webSwitchFailed ? null : (
            <> {t('wapiPlanReadOnly.sellerHint')}</>
          )}
        </p>
      </div>
      {offersWeb || canUpgradePlan ? (
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          {canUpgradePlan ? (
            <Button
              type="button"
              size="sm"
              color={offersWeb ? 'secondary' : 'primary'}
              onClick={onUpgradePlan}
            >
              {t('wapiPlanReadOnly.upgradeCta')}
            </Button>
          ) : null}
          {offersWeb ? (
            <Button
              type="button"
              size="sm"
              color="primary"
              onClick={onReplyByWhatsAppWeb}
            >
              {t('wapiPlanReadOnly.replyByWebCta')}
            </Button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
